import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/features/auth/store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Send cookies (refresh token) on cross-origin requests
});

// ─── Request Interceptor: Inject Access Token ───────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const state = useAuthStore.getState();
    if (state.accessToken) {
      config.headers.Authorization = `Bearer ${state.accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response Interceptor: Handle 401 & Auto-Refresh ────────────────────
//
// PREVIOUS BUG — Token Reuse Race Condition:
// The old implementation used a boolean `isRefreshing` flag with an explicit
// queue (`failedQueue` + `processQueue`). When the refresh completed,
// `isRefreshing` was set to `false` in the `finally` block BEFORE the queued
// requests' HTTP retries had finished. If a queued retry hit a fresh 401,
// it entered the interceptor with `isRefreshing = false` and sent a SECOND
// refresh request using the SAME (now-revoked) refresh token. The backend
// detected token reuse and terminated the session.
//
// FIX — Store the refresh Promise:
// Instead of a boolean flag + queue, store the in-flight refresh Promise
// itself. Every concurrent 401 latches onto the same Promise via `.then()`.
// This guarantees only ONE refresh request ever executes, eliminating the
// race condition entirely. No explicit queue management needed — the
// Promise chain naturally sequences retries.
//
// Flow:
// 1. A request gets a 401 response
// 2. If it's a login or refresh request, reject immediately
// 3. If a refresh is already in-flight (promise stored), latch onto it
// 4. Otherwise, create and store the refresh Promise
// 5. POST to /auth/refresh — the HttpOnly refresh cookie is sent automatically
// 6. On success: update access token in store, retry all queued requests
// 7. On failure: reject all queued requests

let refreshPromise: Promise<{ access: { accessToken: string } }> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only attempt refresh on 401 and if we haven't retried already
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh if the failing request is itself a refresh or login.
    // IMPORTANT: Do NOT call clearSession() here. The AuthProvider's deferred
    //   clearSession (using failureCount) handles session cleanup after all
    //   React Query retries are exhausted. Calling clearSession() in the
    //   interceptor sets status to 'unauthenticated' before the retry can
    //   execute, creating a race condition that sends the user to /login.
    const url = originalRequest.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
      return Promise.reject(error);
    }

    // If a refresh is already in-flight, latch onto the EXISTING Promise.
    // This ensures only ONE refresh request is ever sent, preventing token
    // reuse detection on the backend.
    if (refreshPromise) {
      originalRequest._retry = true;
      return refreshPromise.then((payload) => {
        originalRequest.headers.Authorization = `Bearer ${payload.access.accessToken}`;
        return apiClient(originalRequest);
      });
    }

    // ─── Start a new refresh flow (first 401 wins) ──────────────

    originalRequest._retry = true;

    // Create the refresh Promise and store it so concurrent 401s latch on
    refreshPromise = (async (): Promise<{ access: { accessToken: string } }> => {
      const { refreshTokenApi } = await import(
        '@/features/auth/services/auth.api'
      );
      // Cookie is sent automatically via withCredentials: true
      const payload = await refreshTokenApi();

      // Update store with new access token
      useAuthStore.getState().setTokens({
        access: payload.access,
      });

      return payload;
    })();

    try {
      const payload = await refreshPromise;

      // Retry the original request with the new access token
      originalRequest.headers.Authorization = `Bearer ${payload.access.accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // IMPORTANT: Do NOT call clearSession() here either.
      // AuthProvider's deferred clearSession (failureCount >= 2) handles
      // this after all retries are exhausted.
      return Promise.reject(refreshError);
    } finally {
      // Clear the stored promise so future 401s trigger a new refresh
      refreshPromise = null;
    }
  },
);

// Helper: export for testing
export function __resetRefreshStateForTesting(): void {
  refreshPromise = null;
}

export default apiClient;
